import type { Rect, TextItem } from "./types";

/**
 * Pure text-matching for PdfProvenance. Deliberately has NO pdfjs import —
 * pdf.js is only usable in a browser/worker environment (canvas + worker
 * feature-detection at module load), so keeping this file pure lets it run
 * under plain jest/node with zero mocking, and lets us unit-test the
 * matching logic (the part most likely to have edge-case bugs) in
 * isolation from rendering.
 */

// Every hyphen-ish glyph we've seen in real extracted part numbers, folded
// to a plain ASCII '-' before comparison. The brief calls out hyphen
// (U+002D), en dash (U+2013) and non-breaking hyphen (U+2011) specifically;
// figure dash/em dash/minus sign are included too since pdf.js text
// extraction from different font encodings has been seen to produce them
// for what is visually just "a dash" in a part number.
const HYPHEN_CHARS = new Set([
  "-", // U+002D hyphen-minus
  "‐", // hyphen
  "‑", // non-breaking hyphen
  "‒", // figure dash
  "–", // en dash
  "—", // em dash
  "−", // minus sign
]);

// Trailing punctuation to strip from a *query* before matching (a caller
// passing "Net 30 Days." or "334-0002-005," shouldn't fail to match just
// because the extracted value string carries a trailing full stop/comma
// picked up from surrounding prose).
const TRAILING_PUNCTUATION_RE = /[.,;:!?]+$/;

const WORD_CHAR_RE = /[a-z0-9]/;

function isWordChar(ch: string | undefined): boolean {
  return !!ch && WORD_CHAR_RE.test(ch);
}

/**
 * Lowercase + fold hyphen variants + collapse whitespace runs, while
 * tracking which original character each output character came from. The
 * map lets callers translate a match found in the normalized string back
 * to an offset in the *original* (un-normalized) string, which is what we
 * need to proportionally trim a TextItem's rect to just the matched
 * substring.
 */
function normalizeWithMap(raw: string): { norm: string; map: number[] } {
  const norm: string[] = [];
  const map: number[] = [];
  let lastWasSpace = false;
  for (let i = 0; i < raw.length; i++) {
    const original = raw[i];
    let ch = original.toLowerCase();
    if (HYPHEN_CHARS.has(original)) ch = "-";
    if (/\s/.test(ch)) {
      if (lastWasSpace) continue; // collapse runs of whitespace to one space
      ch = " ";
      lastWasSpace = true;
    } else {
      lastWasSpace = false;
    }
    norm.push(ch);
    map.push(i);
  }
  return { norm: norm.join(""), map };
}

/** Normalize a search query: same folding as items, plus trimming and
 * stripping trailing punctuation (queries are extracted *values*, which
 * may carry punctuation the source document's own text doesn't). */
function normalizeQuery(query: string): string {
  const { norm } = normalizeWithMap(query.trim());
  return norm.replace(TRAILING_PUNCTUATION_RE, "").trim();
}

// How close two items' y-coordinates need to be (relative to their own
// height) to be considered "the same visual line". Items on one line
// commonly have slightly different y due to baseline/font-size
// differences (e.g. a bold label next to normal body text), so this can't
// be an exact match — but it also shouldn't be so loose that two genuinely
// different lines merge.
const LINE_Y_TOLERANCE_FACTOR = 0.6;

// How large a horizontal gap between two same-line items can be before we
// treat it as a deliberate word-space rather than adjacent glyphs of one
// split token (e.g. a part number broken across two TextItems by pdf.js
// because of a font/kerning run boundary, with ~zero gap between them).
// Expressed relative to item height as a rough proxy for "one character
// width" — exact character metrics aren't available to this pure module.
const TOUCH_GAP_FACTOR = 0.35;

interface ItemNorm {
  item: TextItem;
  norm: string;
  map: number[];
}

/**
 * Group text items into visual lines (same y within tolerance), each line
 * sorted left-to-right. This is the basis for joining adjacent items into
 * one searchable string per line — matching is never attempted *across*
 * lines, since a value split across two visually stacked lines with no
 * continuation cue isn't the "adjacent items" case this is built for.
 */
function groupIntoLines(items: TextItem[]): TextItem[][] {
  const sorted = [...items].sort((a, b) => a.y - b.y);
  const lines: TextItem[][] = [];
  for (const item of sorted) {
    const line = lines.find((candidate) => {
      const ref = candidate[0];
      const tolerance = Math.max(ref.h, item.h) * LINE_Y_TOLERANCE_FACTOR;
      return Math.abs(ref.y - item.y) <= tolerance;
    });
    if (line) line.push(item);
    else lines.push([item]);
  }
  for (const line of lines) line.sort((a, b) => a.x - b.x);
  return lines;
}

interface PosMapEntry {
  /** Index into the line's item array, or -1 for a synthetic separator
   * character that doesn't belong to any single item (inserted where two
   * items have a visible gap between them but neither item's own text
   * already supplies the space). */
  itemIdx: number;
  /** Offset into that item's *original* (un-normalized) string. */
  orig: number;
}

/** Build one searchable string for a line, plus a per-character map back
 * to (item, original-offset), joining adjacent items either directly
 * (touching glyphs — e.g. a split part number) or with an inserted space
 * (a visible gap — e.g. separate words). */
function buildLineText(line: TextItem[]): { text: string; map: PosMapEntry[]; itemNorms: ItemNorm[] } {
  const itemNorms: ItemNorm[] = line.map((item) => ({ item, ...normalizeWithMap(item.str) }));
  let text = "";
  const map: PosMapEntry[] = [];

  itemNorms.forEach((entry, idx) => {
    if (idx > 0) {
      const prev = itemNorms[idx - 1];
      const prevEndsSpace = prev.norm.endsWith(" ");
      const thisStartsSpace = entry.norm.startsWith(" ");
      if (!prevEndsSpace && !thisStartsSpace) {
        const gap = entry.item.x - (prev.item.x + prev.item.w);
        const touchThreshold = Math.max(prev.item.h, entry.item.h) * TOUCH_GAP_FACTOR;
        if (gap > touchThreshold) {
          text += " ";
          map.push({ itemIdx: -1, orig: -1 });
        }
        // else: gap is small enough that these items are treated as
        // glyphs of one token (e.g. "334-0002-" + "005") and joined with
        // no separator at all.
      }
    }
    for (let i = 0; i < entry.norm.length; i++) {
      text += entry.norm[i];
      map.push({ itemIdx: idx, orig: entry.map[i] });
    }
  });

  return { text, map, itemNorms };
}

/** Build the Rect for one match span, given the (inclusive) first/last
 * PosMapEntry it covers. Trims proportionally by character offset within
 * the covering item(s) — not pixel-perfect (a monospace assumption), but
 * enough to visually point at the right part of a longer run of text. */
function buildRect(itemNorms: ItemNorm[], spanEntries: PosMapEntry[]): Rect {
  const first = spanEntries[0];
  const last = spanEntries[spanEntries.length - 1];
  const firstItem = itemNorms[first.itemIdx].item;
  const lastItem = itemNorms[last.itemIdx].item;

  const startFrac = firstItem.str.length > 0 ? first.orig / firstItem.str.length : 0;
  const endFrac = lastItem.str.length > 0 ? (last.orig + 1) / lastItem.str.length : 1;

  if (first.itemIdx === last.itemIdx) {
    return {
      x: firstItem.x + startFrac * firstItem.w,
      y: firstItem.y,
      w: Math.max(0, (endFrac - startFrac) * firstItem.w),
      h: firstItem.h,
    };
  }

  const seen = new Set<number>();
  const spannedItems: TextItem[] = [];
  for (const e of spanEntries) {
    if (e.itemIdx === -1 || seen.has(e.itemIdx)) continue;
    seen.add(e.itemIdx);
    spannedItems.push(itemNorms[e.itemIdx].item);
  }

  const x0 = firstItem.x + startFrac * firstItem.w;
  const x1 = lastItem.x + endFrac * lastItem.w;
  const yMin = Math.min(...spannedItems.map((it) => it.y));
  const yMax = Math.max(...spannedItems.map((it) => it.y + it.h));

  return {
    x: x0,
    y: yMin,
    w: Math.max(0, x1 - x0),
    h: Math.max(0, yMax - yMin),
  };
}

/**
 * Find every occurrence of `query` among `items`, returning one Rect per
 * occurrence (so a value that appears twice on the page returns two
 * Rects). Handles:
 *  - a query fully inside one item's text,
 *  - a query spanning two or more adjacent items on the same visual line
 *    (same y within tolerance, x-ordered) — e.g. a part number or a
 *    multi-word phrase split across separate text runs,
 *  - no match — returns [].
 */
export function findMatches(items: TextItem[], query: string): Rect[] {
  const normQuery = normalizeQuery(query);
  if (!normQuery) return [];

  const results: Rect[] = [];
  const lines = groupIntoLines(items);

  for (const line of lines) {
    const { text, map, itemNorms } = buildLineText(line);

    let searchFrom = 0;
    for (;;) {
      const idx = text.indexOf(normQuery, searchFrom);
      if (idx === -1) break;
      const endIdx = idx + normQuery.length;

      const before = text[idx - 1];
      const after = text[endIdx];
      if (isWordChar(before) || isWordChar(after)) {
        // Matched inside a larger token (e.g. "30" inside "330") — reject
        // and keep scanning from the next character.
        searchFrom = idx + 1;
        continue;
      }

      const spanEntries = map.slice(idx, endIdx).filter((e) => e.itemIdx !== -1);
      if (spanEntries.length > 0) {
        results.push(buildRect(itemNorms, spanEntries));
      }
      searchFrom = endIdx;
    }
  }

  return results;
}

/** Convenience batch form of findMatches for a set of queries. */
export function findAllMatches(items: TextItem[], queries: string[]): Map<string, Rect[]> {
  const out = new Map<string, Rect[]>();
  for (const query of queries) {
    out.set(query, findMatches(items, query));
  }
  return out;
}
